import { createTheme } from '@mui/material/styles';
import type {} from '@mui/material/themeCssVarsAugmentation';
import { makeAccessibleColor } from './accessible-color';

interface TenantThemeColors {
  primaryColor?: string | null;
  secondaryColor?: string | null;
}

// Tenant-branded colors (set per tenant in epms.platform) take over the app's
// default palette when provided, so buttons/links/active-states/AppBar tint
// reflect each tenant's brand instead of the fixed default indigo/green pair.
//
// A tenant only ever picks one color, but light and dark mode need different lightness ranges
// to stay legible against their opposite backgrounds — using it raw and unmodified in both (as
// this used to do) is exactly what caused nav section titles, the dashboard banner, and other
// primary-color-derived text to lose contrast in dark mode whenever a tenant's color happened
// to be too dark for a near-black background. makeAccessibleColor remaps only the lightness
// per mode (hue/saturation, i.e. the actual brand identity, are untouched), the same way this
// file's own hardcoded defaults already use a much lighter blue for dark mode than light mode.
const getTheme = (direction: 'ltr' | 'rtl' = 'ltr', tenantColors?: TenantThemeColors) => {
  const primaryColorLight = tenantColors?.primaryColor
    ? makeAccessibleColor(tenantColors.primaryColor, 'light')
    : { main: '#1976D2', contrastText: '#ffffff' };
  const primaryColorDark = tenantColors?.primaryColor
    ? makeAccessibleColor(tenantColors.primaryColor, 'dark')
    : { main: '#90CAF9', contrastText: '#0D1B2A' };
  const secondaryColorLight = tenantColors?.secondaryColor
    ? makeAccessibleColor(tenantColors.secondaryColor, 'light')
    : { main: '#4CAF50', contrastText: '#ffffff' };
  const secondaryColorDark = tenantColors?.secondaryColor
    ? makeAccessibleColor(tenantColors.secondaryColor, 'dark')
    : { main: '#A5D6A7', contrastText: '#0D1B2A' };

  return createTheme({
    direction,
    cssVariables: {
      colorSchemeSelector: 'data-toolpad-color-scheme',
    },
    colorSchemes: {
      light: {
        palette: {
          primary: primaryColorLight,
          secondary: secondaryColorLight,
          background: {
            default: '#f5f7fa',
            paper: '#ffffff',
          },
          text: {
            primary: '#243B53',
            secondary: '#4A5568',
          },
          error: {
            main: '#E57373',
          },
          warning: {
            main: '#FFB74D',
          },
          success: {
            main: '#81C784',
          },
          info: {
            main: '#64B5F6',
          },
        },
      },
      dark: {
        palette: {
          primary: primaryColorDark,
          secondary: secondaryColorDark,
          background: {
            default: '#121212',
            paper: '#1E1E2F',
          },
          text: {
            primary: '#E0E0E0',
            secondary: '#B0BEC5',
          },
          error: {
            main: '#EF9A9A',
          },
          warning: {
            main: '#FFCC80',
          },
          success: {
            main: '#A5D6A7',
          },
          info: {
            main: '#90CAF9',
          },
        },
      },
    },
    shape: {
      borderRadius: 12,
    },
    typography: {
      fontFamily: 'Cairo, "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontSize: '2.5rem',
        fontWeight: 600,
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 600,
      },
      h3: {
        fontSize: '1.75rem',
        fontWeight: 600,
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 600,
      },
      body1: {
        fontSize: '1rem',
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '*': {
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(150, 150, 150, 0.4) transparent',
          },
          '*::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '*::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '*::-webkit-scrollbar-thumb': {
            background: 'rgba(150, 150, 150, 0.4)',
            borderRadius: 4,
          },
          '*::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(150, 150, 150, 0.6)',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 10,
            transition: 'all 0.2s ease',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            transition: 'box-shadow 0.3s ease, background-color 0.3s ease',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            transition: 'transform 0.2s ease, box-shadow 0.3s ease',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 500,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 8,
            fontSize: '0.8rem',
            fontWeight: 500,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: 'none',
            borderLeft: 'none',
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            margin: '2px 8px',
            transition: 'all 0.2s ease',
            '&.Mui-selected': {
              fontWeight: 600,
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: 40,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 10,
              transition: 'box-shadow 0.2s ease',
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
        },
      },
    },
  });
};

export default getTheme;
